import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Linkedin, MessageCircle, ArrowUpRight, CheckCircle2, AlertCircle, Paperclip, X } from 'lucide-react';
import PageHero from '../components/PageHero';
import { COMPANY } from '../data/content';
import { submitEnquiry } from '../services/firestoreService';
import { openExternal } from '../lib/openExternal';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [rfqFile, setRfqFile] = useState(null);
  const fileRef = useRef(null);
  const [status, setStatus] = useState({ type: null, msg: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus({ type: null });
    try {
      await submitEnquiry(form, rfqFile);
      setStatus({ type: 'ok', msg: 'Thank you — your enquiry has been received. Our team will be in touch shortly.' });
      setForm({ name: '', email: '', phone: '', company: '', message: '' });
      setRfqFile(null);
    } catch (err) {
      setStatus({ type: 'err', msg: 'Could not submit right now. Please email solutions@tricoliteenergy.in or call us directly.' });
    } finally { setBusy(false); }
  };
  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Exact place names resolved from the user's own Google Maps short links
  const MAP1_EMBED = `https://maps.google.com/maps?q=${encodeURIComponent('TRICOLITE ENERGY Solutions Pvt Ltd, 216, Success Towers, Golf Course Extention Road, Sector 65, Gurugram, Haryana 122101')}&t=&z=18&ie=UTF8&iwloc=B&output=embed`;
  const MAP2_EMBED = `https://maps.google.com/maps?q=${encodeURIComponent('Tricolite Electrical Industries Limited-LV Plant, Indospace Industrial and Logistics Park, B-50, Village Pahasour, Badli, Faizabad, Haryana 124105')}&t=&z=16&ie=UTF8&iwloc=B&output=embed`;
  const MAP3_EMBED = `https://maps.google.com/maps?q=${encodeURIComponent('Tricolite Electrical Industries Limited - MV Plant, Reliance MET, Plot No.1 2, Street No.11, Jhajjar Rd, Sector 7, Farukh Nagar, Saundhi, Haryana 124105')}&t=&z=16&ie=UTF8&iwloc=B&output=embed`;

  const LOCATIONS = [
    {
      label: 'Corporate Office',
      name: 'Tricolite Energy Solutions',
      addr: '216, Success Towers, Golf Course Extension Road, Sector 65, Gurugram — 122018',
      shortLink: 'https://maps.app.goo.gl/ZRmcVe2zTK44W3xN9',
      embed: MAP1_EMBED,
      mapTitle: 'Map 1 — Visit us in Gurugram',
      mapAddr: '216, Success Towers, Golf Course Extension Road, Sector 65, Gurugram',
    },
    {
      label: 'Manufacturing Unit · Badli',
      name: 'Tricolite Electrical Industries — LV Plant',
      addr: 'B-50, IndoSpace Industrial & Logistics Park, Village Pahasour, Badli, Haryana — 124105',
      shortLink: 'https://maps.app.goo.gl/TuVEyuU31U4fgy9U7',
      embed: MAP2_EMBED,
      mapTitle: 'Map 2 — Manufacturing Unit, Badli',
      mapAddr: 'B-50, IndoSpace Industrial & Logistics Park, Village Pahasour, Badli, Haryana',
    },
    {
      label: 'Manufacturing Unit · Jhajjar',
      name: 'Tricolite Electrical Industries — MV Plant',
      addr: 'Plot No. 1 & 2, Street No.11, Jhajjar Road, Sector 7, Farukh Nagar, Saundhi, Haryana — 124105',
      shortLink: 'https://maps.app.goo.gl/ii4Jn6RGys5rSP3f9',
      embed: MAP3_EMBED,
      mapTitle: 'Map 3 — Manufacturing Unit, Jhajjar',
      mapAddr: 'Plot 1 & 2, Jhajjar Road, Sector 7, Farukh Nagar, Saundhi, Haryana',
    },
  ];

  const mapsLink = LOCATIONS[0].shortLink;

  return (
    <main>
      <PageHero eyebrow="Partner with us" title="Let's energize your" accent="next project." desc="Reach out to our team. Drop us a message, give us a call, or connect via WhatsApp — we'll get back as soon as possible." />

      <section className="px-6 md:px-10 lg:px-14 py-16 lg:py-24 bg-[var(--cream)]">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Reach Us</div>
            <h2 className="font-display text-[clamp(28px,3.4vw,46px)] leading-[1.05] font-bold tracking-tight mb-10">
              <span className="text-[var(--blue)]">Tricolite Energy</span><br/><span className="font-serif-it text-[var(--orange)]">Solutions</span>
            </h2>
            <div className="space-y-7">
              <ContactRow icon={Mail} label="Email" main={<a href={`mailto:${COMPANY.email}`} className="text-[var(--orange)]">{COMPANY.email}</a>} />
              <ContactRow icon={Phone} label="Phone" main={<a href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phonePrimary}</a>} />
              <ContactRow icon={MessageCircle} label="WhatsApp" main={<a href={COMPANY.whatsapp} onClick={openExternal(COMPANY.whatsapp)} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--orange)]" data-testid="contact-whatsapp-number">{COMPANY.whatsappDisplay}</a>} />
              <ContactRow icon={MapPin} label="Head Office" main={<a href={mapsLink} onClick={openExternal(mapsLink)} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--orange)]">216, Success Towers,<br/>Golf Course Extension Road,<br/>Gurugram, Haryana – 122018, India</a>} />
              <ContactRow icon={MessageCircle} label="Quick Connect" main={
                <div className="flex flex-wrap gap-3 mt-1">
                  <a href={COMPANY.whatsapp} onClick={openExternal(COMPANY.whatsapp)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white hover:border-[var(--orange)] text-[13px] font-medium"><MessageCircle className="w-4 h-4"/> WhatsApp</a>
                  <a href={COMPANY.linkedin} onClick={openExternal(COMPANY.linkedin)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white hover:border-[var(--orange)] text-[13px] font-medium"><Linkedin className="w-4 h-4"/> LinkedIn</a>
                  <a href={mapsLink} onClick={openExternal(mapsLink)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white hover:border-[var(--orange)] text-[13px] font-medium"><MapPin className="w-4 h-4"/> Google Maps</a>
                </div>
              } />
            </div>
            <div className="mt-10 p-6 rounded-2xl bg-[var(--cream-deep)] border border-[var(--line)]">
              <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-2">Certifications</div>
              <div className="font-display font-bold text-[18px] heading-ink">ISO 9001 · 14001 · 45001</div>
              <div className="text-[13px] text-[var(--text-mid)] mt-1">Quality · Environment · Health & Safety</div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form onSubmit={submit} className="bg-white border border-[var(--line)] rounded-3xl p-8 md:p-10">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-3">Get In Touch</div>
              <h3 className="font-display font-bold text-[26px] md:text-[30px] leading-tight mb-2 heading-ink">Send us an enquiry</h3>
              <p className="text-[14px] text-[var(--text-mid)] mb-8">Tell us about your project and our team will be in touch.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Name *"><input required value={form.name} onChange={onChange('name')} className="cf-input" placeholder="Your name" /></Field>
                <Field label="Company / Organisation"><input value={form.company} onChange={onChange('company')} className="cf-input" placeholder="Your company" /></Field>
                <Field label="Email *"><input required type="email" value={form.email} onChange={onChange('email')} className="cf-input" placeholder="you@company.com" /></Field>
                <Field label="Mobile"><input value={form.phone} onChange={onChange('phone')} className="cf-input" placeholder="+91" /></Field>
              </div>
              <Field label="Message *"><textarea required value={form.message} onChange={onChange('message')} className="cf-textarea" placeholder="Tell us about your project, location, scope or any questions..." /></Field>

              {/* Attach RFQ */}
              <div className="mt-4">
                <span className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-2 font-medium">Attach RFQ</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                  className="hidden"
                  data-testid="rfq-file-input"
                  onChange={(e) => setRfqFile(e.target.files[0] || null)}
                />
                {rfqFile ? (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--cream-deep)] border border-[var(--line)]">
                    <Paperclip className="w-4 h-4 text-[var(--blue)] shrink-0"/>
                    <span className="text-[14px] text-[var(--ink)] truncate flex-1">{rfqFile.name}</span>
                    <span className="text-[12px] text-[var(--text-mid)] shrink-0">{(rfqFile.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => { setRfqFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="w-7 h-7 rounded-full bg-white border border-[var(--line)] flex items-center justify-center hover:border-red-400 transition-colors shrink-0" data-testid="rfq-remove-btn">
                      <X className="w-3.5 h-3.5 text-[var(--text-mid)]"/>
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-[var(--line)] bg-[var(--cream-deep)] hover:border-[var(--blue)] text-[14px] text-[var(--text-mid)] hover:text-[var(--blue)] transition-colors w-full" data-testid="rfq-attach-btn">
                    <Paperclip className="w-4 h-4"/> Attach RFQ document (PDF, DOC, XLS, etc.)
                  </button>
                )}
              </div>

              <button type="submit" disabled={busy} className="mt-5 inline-flex items-center gap-2 bg-[var(--blue)] hover:bg-[var(--orange)] disabled:opacity-60 text-white pl-6 pr-7 py-4 rounded-full font-medium transition-colors btn-shine group" data-testid="send-rfq-btn">
                <span className="btn-dot text-white"/>
                <span className="btn-label">{busy ? 'Submitting…' : 'Send RFQ'}</span>
                <ArrowUpRight className="w-4 h-4 btn-arrow"/>
              </button>
              {status.type === 'ok' && (
                <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-[var(--orange)]/10 border border-[var(--orange)]/30 text-[var(--ink)]"><CheckCircle2 className="w-5 h-5 text-[var(--orange)] mt-0.5"/><div className="text-[14px]">{status.msg}</div></div>
              )}
              {status.type === 'err' && (
                <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800"><AlertCircle className="w-5 h-5 mt-0.5"/><div className="text-[14px]">{status.msg}</div></div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Office Locations + Maps */}
      <section className="px-6 md:px-10 lg:px-14 pb-20 bg-[var(--cream)]" data-testid="map-section">
        <div className="max-w-[1500px] mx-auto">

          {/* Section header */}
          <div className="mb-12 reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-3">Our Locations</div>
            <h3 className="font-display text-[clamp(30px,3.8vw,52px)] font-bold leading-tight tracking-tight">
              Find us — <span className="font-serif-it text-[var(--orange)]">across NCR.</span>
            </h3>
          </div>

          {/* 3 address cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 reveal">
            {LOCATIONS.map((loc, i) => (
              <a key={i} href={loc.shortLink} onClick={openExternal(loc.shortLink)} target="_blank" rel="noopener noreferrer"
                className="group block p-6 rounded-2xl border border-[var(--line)] bg-white hover:border-[var(--orange)] hover:shadow-md transition-all duration-300"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-2">{loc.label}</div>
                <div className="font-display font-bold text-[15px] mb-2 text-[var(--ink)]">{loc.name}</div>
                <div className="text-[13px] text-[var(--text-mid)] leading-relaxed">{loc.addr}</div>
                <div className="mt-4 text-[12px] font-medium text-[var(--orange)] flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Open in Maps <ArrowUpRight className="w-3.5 h-3.5"/>
                </div>
              </a>
            ))}
          </div>

          {/* 3 embedded maps */}
          {LOCATIONS.map((loc, i) => (
            <div key={i} className={`reveal ${i < LOCATIONS.length - 1 ? 'mb-10' : ''}`}>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--orange)] mb-1">{loc.mapTitle}</div>
                  <div className="text-[15px] font-semibold text-[var(--ink)]">{loc.mapAddr}</div>
                </div>
                <a href={loc.shortLink} onClick={openExternal(loc.shortLink)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--orange)] hover:text-[var(--ink)] transition-colors shrink-0"
                  data-testid={`open-maps-link-${i+1}`}
                >
                  Open in Google Maps <ArrowUpRight className="w-3.5 h-3.5"/>
                </a>
              </div>
              <div className="rounded-3xl overflow-hidden border border-[var(--line)] shadow-md" style={{ height: '480px' }}>
                <iframe
                  title={loc.mapTitle}
                  src={loc.embed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`map-iframe-${i+1}`}
                />
              </div>
            </div>
          ))}

        </div>
      </section>
    </main>
  );
}

function ContactRow({ icon: Icon, label, main, sub }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-white border border-[var(--line)] flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[var(--orange)]"/></div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[var(--text-light)]">{label}</div>
        <div className="font-display font-semibold text-[18px] mt-1 break-words text-[var(--ink)]">{main}</div>
        {sub && <div className="text-[13px] text-[var(--text-mid)] mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-4">
      <span className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-2 font-medium">{label}</span>
      {children}
    </label>
  );
}
